import type { GetOrganizationInvoice } from "@/server-types";
import {
  hasCapability,
  type ProviderCapabilities,
} from "../providerCapabilities";

type ChangePaymentMethodInvoice = Pick<
  NonNullable<GetOrganizationInvoice["response"]>,
  "type" | "canceled"
> & { readOnly?: boolean };

// Estimates (predračun) and credit notes have no payments, so changing the
// payment method 500s on the backend — only real invoices expose the action.
export function canChangeInvoicePaymentMethod(
  invoice: ChangePaymentMethodInvoice | null | undefined,
  capabilities?: Partial<ProviderCapabilities> | null,
): boolean {
  if (!invoice) return false;
  if (invoice.readOnly) return false;
  if (!hasCapability(capabilities, "paymentEdit")) return false;
  if (invoice.canceled) return false;
  if (invoice.type === "estimate") return false;
  if (invoice.type === "credit-note") return false;
  return true;
}

type EmailRecipientDocumentClient = {
  customerData: { email?: string | null } | null;
  companyData: { email?: string | null } | null;
};

export type InvoiceEmailRecipient =
  | { status: "no-recipient" }
  | { status: "missing-email"; recipient: "customer" | "company" }
  | { status: "ready"; email: string };

export function getInvoiceEmailRecipient(
  documentClient: EmailRecipientDocumentClient | null | undefined,
): InvoiceEmailRecipient {
  const customerData = documentClient?.customerData;
  const companyData = documentClient?.companyData;

  if (customerData == null && companyData == null) {
    return { status: "no-recipient" };
  }

  const email = customerData?.email?.trim() || companyData?.email?.trim();

  if (email) return { status: "ready", email };

  return {
    status: "missing-email",
    recipient: customerData != null ? "customer" : "company",
  };
}

type ShareBillingDocument = {
  id: string;
  type: string;
  referencedDocuments?: { id: string; type: string }[];
};

// An estimate converted outside Lime leaves its shares on the estimate's id.
export function getInvoiceShareDocumentIds(
  invoice: ShareBillingDocument | undefined,
): string[] {
  if (invoice?.type !== "invoice") return [];

  return [
    invoice.id,
    ...(invoice.referencedDocuments ?? [])
      .filter((document) => document.type === "estimate")
      .map((document) => document.id),
  ];
}

type InvoiceSubscriptionShare = { paidAt: Date | string | null };

export function getSharesToMarkUnpaidOnCancel<
  T extends InvoiceSubscriptionShare,
>(shares: T[] | null | undefined): T[] {
  return (shares ?? []).filter((share) => share.paidAt != null);
}

export function getSharesToMarkPaidOnPayment<
  T extends InvoiceSubscriptionShare,
>(shares: T[] | null | undefined): T[] {
  return (shares ?? []).filter((share) => share.paidAt == null);
}
